import type {
  Topic,
  Message,
  DailyJournal,
  CreateTopicRequest,
  UpdateJournalRequest,
} from "@/types";

const API_BASE = import.meta.env.VITE_API_URL || "/api";

/**
 * Handles API response and throws on error
 *
 * @param response - Fetch response object
 * @returns Parsed JSON response
 * @throws Error if response is not ok
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * Creates authorization headers with JWT token
 *
 * @param token - Clerk JWT token
 * @returns Headers object with Authorization
 */
function authHeaders(token: string): HeadersInit {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
}

/**
 * API client for Study Manager backend
 */
export const api = {
  /**
   * Fetch all topics for the current user
   *
   * @param token - Clerk JWT token
   * @returns Array of topics
   */
  async getTopics(token: string): Promise<Topic[]> {
    const response = await fetch(`${API_BASE}/topics`, {
      headers: authHeaders(token),
    });
    return handleResponse<Topic[]>(response);
  },

  /**
   * Create a new topic
   *
   * @param token - Clerk JWT token
   * @param data - Topic creation data
   * @returns Created topic
   */
  async createTopic(token: string, data: CreateTopicRequest): Promise<Topic> {
    const response = await fetch(`${API_BASE}/topics`, {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify(data),
    });
    return handleResponse<Topic>(response);
  },

  /**
   * Fetch messages for a specific topic
   *
   * @param token - Clerk JWT token
   * @param topicId - Topic ID to fetch messages for
   * @returns Array of messages
   */
  async getMessages(token: string, topicId: string): Promise<Message[]> {
    const response = await fetch(`${API_BASE}/topics/${topicId}/messages`, {
      headers: authHeaders(token),
    });
    return handleResponse<Message[]>(response);
  },

  /**
   * Send a message and stream the AI response
   *
   * @param token - Clerk JWT token
   * @param topicId - Topic ID to send message to
   * @param content - Message content
   * @param onChunk - Callback for each streamed chunk
   */
  async sendMessage(
    token: string,
    topicId: string,
    content: string,
    onChunk: (chunk: string) => void
  ): Promise<void> {
    const response = await fetch(`${API_BASE}/chat`, {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({ topicId, content }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("No response body");
    }

    const decoder = new TextDecoder();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      onChunk(chunk);
    }
  },

  /**
   * Fetch recent journals
   *
   * @param token - Clerk JWT token
   * @param limit - Maximum number of journals to return
   * @returns Array of recent journals
   */
  async getRecentJournals(
    token: string,
    limit: number = 10
  ): Promise<DailyJournal[]> {
    const response = await fetch(`${API_BASE}/journals?limit=${limit}`, {
      headers: authHeaders(token),
    });
    return handleResponse<DailyJournal[]>(response);
  },

  /**
   * Fetch all journals for the current user
   *
   * @param token - Clerk JWT token
   * @returns Array of all journals
   */
  async getAllJournals(token: string): Promise<DailyJournal[]> {
    const response = await fetch(`${API_BASE}/journals`, {
      headers: authHeaders(token),
    });
    return handleResponse<DailyJournal[]>(response);
  },

  /**
   * Fetch a journal by date
   *
   * @param token - Clerk JWT token
   * @param date - Date string (YYYY-MM-DD format)
   * @returns Journal for the specified date or null
   */
  async getJournalByDate(
    token: string,
    date: string
  ): Promise<DailyJournal | null> {
    const response = await fetch(`${API_BASE}/journals/${date}`, {
      headers: authHeaders(token),
    });
    if (response.status === 404) {
      return null;
    }
    return handleResponse<DailyJournal>(response);
  },

  /**
   * Update an existing journal
   *
   * @param token - Clerk JWT token
   * @param journalId - Journal ID to update
   * @param data - Update data
   * @returns Updated journal
   */
  async updateJournal(
    token: string,
    journalId: string,
    data: UpdateJournalRequest
  ): Promise<DailyJournal> {
    const response = await fetch(`${API_BASE}/journals/${journalId}`, {
      method: "PATCH",
      headers: authHeaders(token),
      body: JSON.stringify(data),
    });
    return handleResponse<DailyJournal>(response);
  },

  /**
   * Generate a daily journal for a specific date
   *
   * @param token - Clerk JWT token
   * @param date - Date string (YYYY-MM-DD format)
   * @param regenerate - If true, regenerate and overwrite existing journal
   * @returns Generated journal
   */
  async generateDailyJournal(
    token: string,
    date: string,
    regenerate: boolean = false
  ): Promise<DailyJournal> {
    const response = await fetch(`${API_BASE}/journals/generate`, {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({ date, regenerate }),
    });
    return handleResponse<DailyJournal>(response);
  },
};

